import React, { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';

import { SectionHeader, InfoLabel, ErrorMessage } from '@/components/common';
import { useUpdateCourse } from '@/hooks/courses.hook';
import { useInstructorList } from '@/hooks/instructor.hook';
import { successToast, errorToast } from '@/common/toast';
import { LANGUAGE_OPTIONS } from '@/constants/courses';

/**
 * 과정 수정 모달 컴포넌트
 * 
 * 과정 정보와 이미지/파일을 수정할 수 있는 모달입니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {Object} props.courseData - 수정할 과정 데이터
 * @param {Function} props.onSuccess - 수정 성공 후 콜백
 */
const CourseEditModal = ({
  open,
  onClose,
  courseData,
  onSuccess,
}) => {
  const [uploadProgress, setUploadProgress] = useState(false);
  const [thumbnailImg, setThumbnailImg] = useState(null);
  const [introImg, setIntroImg] = useState(null);
  const [lessonPlanFile, setLessonPlanFile] = useState(null);

  const updateCourseMutation = useUpdateCourse();
  const { data: instructorList = [], isLoading: instructorLoading } = useInstructorList();

  // 폼 관리
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    defaultValues: {
      courseName: '',
      courseCategory: 'SOFTWARE_SECURITY',
      courseCount: 1,
      courseObj: '',
      languageId: 0,
      freeYn: false,
      hasTraining: false,
      instId: '',
    },
  });

  // 과정 데이터 로드 시 폼에 값 설정
  useEffect(() => {
    if (open && courseData) {
      console.log(courseData);
      reset({
        courseName: courseData.courseName || '',
        courseCategory: courseData.category || 'SOFTWARE_SECURITY',
        courseCount: courseData.courseCount || 1,
        courseObj: courseData.courseObj || '',
        languageId: courseData.language?.id || 0,
        freeYn: courseData.freeYn || false,
        hasTraining: courseData.hasTraining || false,
        instId: courseData.instructor?.instId || '',
      });
      // 파일 초기화
      setThumbnailImg(null);
      setIntroImg(null);
      setLessonPlanFile(null);
    }
  }, [open, courseData, reset]);

  /**
   * 파일 선택 핸들러
   */
  const handleFileSelect = useCallback((fieldName, event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (fieldName === 'thumbnailImg') {
        setThumbnailImg(file);
      } else if (fieldName === 'introImg') {
        setIntroImg(file);
      } else if (fieldName === 'lessonPlanFile') {
        setLessonPlanFile(file);
      }
    }
  }, []);

  /**
   * 과정 수정 핸들러
   */
  const onSubmit = useCallback(
    async (data) => {
      if (!courseData) {
        errorToast('과정 정보를 찾을 수 없습니다.');
        return;
      }

      setUploadProgress(true);
      try {
        await updateCourseMutation.mutateAsync({
          courseId: parseInt(courseData.courseId),
          courseName: data.courseName,
          category: data.courseCategory,
          courseCount: parseInt(data.courseCount) || 1,
          courseObj: data.courseObj || '',
          languageId: parseInt(data.languageId) || 0,
          freeYn: data.freeYn || false,
          hasTraining: data.hasTraining || false,
          instId: data.instId ? parseInt(data.instId) : null,
          thumbnailImg: thumbnailImg instanceof File ? thumbnailImg : null,
          introImg: introImg instanceof File ? introImg : null,
          lessonPlanFile: lessonPlanFile instanceof File ? lessonPlanFile : null,
        });

        successToast('과정 정보가 수정되었습니다.');
        onSuccess?.();
        onClose();
      } catch (error) {
        errorToast(`과정 정보 수정에 실패했습니다. - ${error.response?.data?.msg}`);
      } finally {
        setUploadProgress(false);
      }
    },
    [courseData, thumbnailImg, introImg, lessonPlanFile, updateCourseMutation, onSuccess, onClose]
  );

  /**
   * 모달 닫기 핸들러
   */
  const handleClose = useCallback(() => {
    if (!uploadProgress) {
      onClose();
    }
  }, [uploadProgress, onClose]);

  if (!courseData) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        과정 수정
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)} id="course-edit-form">
          <Stack spacing={3}>
            {/* 과정 정보 */}
            <Box>
              <SectionHeader sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  과정 정보
                </Typography>
              </SectionHeader>
              <Grid container spacing={2}>
                {/* 과정명 */}
                <Grid item xs={12}>
                  <InfoLabel>과정명 *</InfoLabel>
                  <Controller
                    name="courseName"
                    control={control}
                    rules={{ required: '과정명을 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="과정명을 입력하세요"
                        error={!!errors.courseName}
                        helperText={errors.courseName?.message}
                        disabled={uploadProgress}
                      />
                    )}
                  />
                </Grid>

                {/* 카테고리 */}
                <Grid item xs={12} sm={6}>
                  <InfoLabel>카테고리 *</InfoLabel>
                  <Controller
                    name="courseCategory"
                    control={control}
                    rules={{ required: '카테고리를 선택해주세요.' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.courseCategory} disabled={uploadProgress}>
                        <Select {...field}>
                          <MenuItem value='SOFTWARE_SECURITY'>소프트웨어 개발보안 교육</MenuItem>
                          <MenuItem value='INFORMATION_SECURITY'>정보보안 교육</MenuItem>
                          <MenuItem value='PROGRAMMING'>프로그래밍 교육</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                  <ErrorMessage errors={errors} name='courseCategory' />
                </Grid>

                {/* 언어 */}
                <Grid item xs={12} sm={6}>
                  <InfoLabel>언어 *</InfoLabel>
                  <Controller
                    name="languageId"
                    control={control}
                    rules={{ required: '언어를 선택해주세요.' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.languageId} disabled={uploadProgress}>
                        <Select {...field} value={field.value || 0}>
                          {LANGUAGE_OPTIONS.map((lang) => (
                            <MenuItem key={lang.languageId} value={lang.languageId}>
                              {lang.languageName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <ErrorMessage errors={errors} name='languageId' />
                </Grid>

                {/* 과정 수 */}
                <Grid item xs={12} sm={6}>
                  <InfoLabel>과정 수 *</InfoLabel>
                  <Controller
                    name="courseCount"
                    control={control}
                    rules={{
                      required: '과정 수를 입력해주세요.',
                      min: { value: 1, message: '과정 수는 1 이상이어야 합니다.' },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        placeholder="1"
                        error={!!errors.courseCount}
                        helperText={errors.courseCount?.message}
                        disabled={uploadProgress}
                      />
                    )}
                  />
                </Grid>

                {/* 강사 */}
                <Grid item xs={12} sm={6}>
                  <InfoLabel>강사 *</InfoLabel>
                  <Controller
                    name="instId"
                    control={control}
                    rules={{ required: '강사를 선택해주세요.' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.instId} disabled={uploadProgress || instructorLoading}>
                        <Select {...field} value={field.value || ''} displayEmpty>
                          <MenuItem value='' disabled>강사를 선택하세요</MenuItem>
                          {instructorLoading ? (
                            <MenuItem disabled>로딩 중...</MenuItem>
                          ) : (
                            instructorList.map((instructor) => (
                              <MenuItem key={instructor.instId} value={instructor.instId}>
                                {instructor.instName || instructor.instId}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <ErrorMessage errors={errors} name='instId' />
                </Grid>

                {/* 무료 여부 */}
                <Grid item xs={12} sm={6}>
                  <InfoLabel>무료 여부</InfoLabel>
                  <Controller
                    name="freeYn"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} disabled={uploadProgress} />}
                        label={field.value ? '무료' : '유료'}
                      />
                    )}
                  />
                </Grid>

                {/* 훈련 보유 */}
                <Grid item xs={12} sm={6}>
                  <InfoLabel>진단 및 대응훈련 연계 여부</InfoLabel>
                  <Controller
                    name="hasTraining"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} disabled={uploadProgress} />}
                        label={field.value ? '연계' : '연계 안함'}
                      />
                    )}
                  />
                </Grid>

                {/* 과정 목표 */}
                <Grid item xs={12}>
                  <InfoLabel>과정 목표 *</InfoLabel>
                  <Controller
                    name="courseObj"
                    control={control}
                    rules={{ required: '과정 목표를 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="'본 강의는 사이버공격에 효과적인 예방과 대응을 위해 SW 개발보안 인식 제고, 전문역량 강화를 목적으로 학습합니다.'와 같은 형태로 작성"
                        error={!!errors.courseObj}
                        helperText={errors.courseObj?.message}
                        disabled={uploadProgress}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* 파일 업로드 */}
            <Box>
              <SectionHeader sx={{ mb: 2 }}>
                <UploadIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" fontWeight={600}>
                  파일 업로드 (변경하려는 파일만 선택)
                </Typography>
              </SectionHeader>
              <Stack spacing={2}>
                {/* 썸네일 이미지 */}
                <Box>
                  <InfoLabel>썸네일 이미지 {courseData.thumbnailImg && '(현재: ' + courseData.thumbnailImg + ')'}</InfoLabel>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('thumbnailImg', e)}
                    disabled={uploadProgress}
                    style={{ display: 'none' }}
                    id="thumbnail-img-input-edit"
                  />
                  <label htmlFor="thumbnail-img-input-edit">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      disabled={uploadProgress}
                      sx={{ mb: 1 }}
                    >
                      {thumbnailImg ? thumbnailImg.name : '썸네일 이미지 변경'}
                    </Button>
                  </label>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    변경할 썸네일 이미지는 10MB 이하 너비 367px, 높이 210px의 이미지여야 합니다.
                  </Alert>
                  {thumbnailImg && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      선택된 파일: {thumbnailImg.name}
                    </Alert>
                  )}
                </Box>

                {/* 소개 이미지 */}
                <Box>
                  <InfoLabel>소개 이미지 {courseData.introImg && '(현재: ' + courseData.introImg + ')'}</InfoLabel>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('introImg', e)}
                    disabled={uploadProgress}
                    style={{ display: 'none' }}
                    id="intro-img-input-edit"
                  />
                  <label htmlFor="intro-img-input-edit">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      disabled={uploadProgress}
                      sx={{ mb: 1 }}
                    >
                      {introImg ? introImg.name : '소개 이미지 변경'}
                    </Button>
                  </label>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    변경할 소개 이미지는 10MB 이하 너비 800px의 이미지여야 합니다.
                  </Alert>
                  {introImg && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      선택된 파일: {introImg.name}
                    </Alert>
                  )}
                </Box>

                {/* 강의 교안 파일 */}
                <Box>
                  <InfoLabel>강의 교안 파일 {courseData.lessonPlanStoredName && '(현재: ' + courseData.lessonPlanOrgName + ')'}</InfoLabel>
                  <input
                    type="file"
                    accept=".zip,.pdf,.doc,.docx,.ppt,.pptx,.md"
                    onChange={(e) => handleFileSelect('lessonPlanFile', e)}
                    disabled={uploadProgress}
                    style={{ display: 'none' }}
                    id="lesson-plan-input-edit"
                  />
                  <label htmlFor="lesson-plan-input-edit">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      disabled={uploadProgress}
                      sx={{ mb: 1 }}
                    >
                      {lessonPlanFile ? lessonPlanFile.name : '강의 교안 변경'}
                    </Button>
                  </label>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    변경할 강의 교안 파일은 50MB 이하의 .zip .pdf, .doc, .docx, .ppt, .pptx, .md 파일이어야 합니다.
                  </Alert>
                  {lessonPlanFile && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      선택된 파일: {lessonPlanFile.name}
                    </Alert>
                  )}
                </Box>
              </Stack>
            </Box>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadProgress}>
          취소
        </Button>
        {/* isDirty는 폼 필드만 반영하므로, 파일만 선택한 경우에도 저장 버튼 활성화 */}
        <Button
          type="submit"
          form="course-edit-form"
          variant="contained"
          startIcon={uploadProgress ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={
            uploadProgress ||
            (!isDirty && !thumbnailImg && !introImg && !lessonPlanFile)
          }
        >
          {uploadProgress ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseEditModal;
